import type { ApiRentalContractState } from 'src/types/rental';

/**
 * The renter's rental clock (spec `rental-clock-v2.2-spec.md` §3): how much time is left,
 * how many overdue days have started, what has been paid for them, what is still owed, and
 * what it would take to get back onto the booking's own tier rate.
 *
 * Pure and unit-tested, for one reason: every number here has an on-chain counterpart, and
 * the plate the renter reads must not drift from what `applyOverdueCharge()` will actually
 * charge. Days started are the contract's own formula
 * (`floor((now − rentalEnd − 3h) / 24h) + 1`); days PAID FOR come from the v2.2 ledger —
 * the moment overdue time is paid through, plus the carry on the day still being paid for —
 * and never from a counter (v2.1 kept a cumulative one, which is exactly what made a second
 * overdue episode look already-paid). Money is never invented here: it is read from the
 * mirror, and the two amounts the renter is asked for are quoted by the contract itself.
 *
 * Not a penalty (spec §1.1): time past the agreed end is rental outside the agreement,
 * charged at the listing's published 1–3 day rate for every started day.
 */

/** `RentalDeal.sol` — OVERDUE_GRACE_PERIOD, the free window after the end date. */
export const OVERDUE_GRACE_SECONDS = 3 * 60 * 60;

export const DAY_SECONDS = 86_400;

/**
 * `ProcessExpiredRentalBookingsUseCase.BOOKING_PAYMENT_TTL_MINUTES` — a Book without Pay is
 * cancelled by the platform cron after this long and its dates are freed (spec §4).
 */
export const BOOKING_PAYMENT_TTL_SECONDS = 30 * 60;

/** What the renter's overdue plate, the top-up button and the extend gate all read. */
export interface RentalOverdueClockView {
  /** v2.1+ daily-charge deal (a v1 deal has no daily rate at all). */
  isDailyModel: boolean;
  /** Past the end date but still inside the 3h grace — nothing charged yet. */
  isInGrace: boolean;
  /** Past the end date and past grace: started days are being charged. */
  isOverdue: boolean;
  /** Seconds until the rental ends (0 once it has). */
  secondsToEnd: number;
  /** Seconds until the first day is charged (0 once grace has expired). */
  secondsToFirstCharge: number;
  /** Overdue days started as of `now`, by the contract's formula (0 before grace expiry). */
  startedDays: number;
  /** Overdue days of THIS episode that are fully paid for (from the mirror's ledger). */
  chargedDays: number;
  /** Base units already taken from the deposit for overdue days. */
  chargedAmount: bigint;
  /** Base units left in the deposit. */
  remainingDeposit: bigint;
  /** The original deposit, in base units. */
  depositAmount: bigint;
  /** The published 1–3 day rate charged per started overdue day, in base units. */
  dailyRate: bigint;
  /** A day has started that the hourly cron has not charged yet. */
  hasUnchargedDay: boolean;
  /**
   * Base units still owed for overdue time that has already started (v2.2): unpaid days
   * plus the unpaid remainder of a day the deposit could only cover in part. Always 0 on
   * older generations, which have no notion of an overdue debt.
   */
  outstandingOverdue: bigint;
  /** The deposit can pay for no further day. */
  isExhausted: boolean;
  /** Nothing has been taken from the deposit. */
  isDepositFull: boolean;
  /** Base units needed to refill the deposit to its original amount (`topUpDeposit`). */
  topUpAmount: bigint;
  /**
   * Past the end date `approveExtension()` reverts unless the deposit is whole AND the
   * overdue is settled (`DepositNotFull` / `OverdueNotSettled`) — the renter must pay up
   * first. Before the end date an extension needs neither.
   */
  needsTopUpForExtension: boolean;
}

const toBigInt = (value: string | null | undefined): bigint => {
  if (!value) {
    return 0n;
  }

  try {
    return BigInt(value);
  } catch {
    return 0n;
  }
};

const EMPTY_VIEW: RentalOverdueClockView = {
  isDailyModel: false,
  isInGrace: false,
  isOverdue: false,
  secondsToEnd: 0,
  secondsToFirstCharge: 0,
  startedDays: 0,
  chargedDays: 0,
  chargedAmount: 0n,
  remainingDeposit: 0n,
  depositAmount: 0n,
  dailyRate: 0n,
  hasUnchargedDay: false,
  outstandingOverdue: 0n,
  isExhausted: false,
  isDepositFull: true,
  topUpAmount: 0n,
  needsTopUpForExtension: false,
};

/** What the deposit has already paid for — the time-independent half of the clock. */
export interface RentalDepositLedger {
  /** v2.1+ daily-charge deal (a v1 deal has no daily rate at all). */
  isDailyModel: boolean;
  /** The v2.2 generation, whose overdue ledger is "paid through" rather than a counter. */
  isPaidThroughModel: boolean;
  /** The original deposit, in base units. */
  depositAmount: bigint;
  /** Base units left in the deposit. */
  remainingDeposit: bigint;
  /** Base units already taken for days past the end date, over the whole deal. */
  chargedAmount: bigint;
  /** Days of the CURRENT overdue episode that are fully paid for. */
  chargedDays: number;
  /**
   * Days past the end date paid for over the WHOLE deal, derived from the money actually
   * taken (`totalCharged / dailyRate`). The bill is a lifetime document: after an
   * extension the current episode starts again at day 1, but the money already spent does
   * not. A part-paid day counts once it is fully paid.
   */
  paidDaysTotal: number;
  /** Base units paid toward a day not yet fully covered (v2.2; 0 otherwise). */
  carryPaid: bigint;
  /** Base units missing from the deposit (`topUpDeposit` refills exactly this much). */
  depositGap: bigint;
  /**
   * Base units to send to `topUpDeposit()` — the deposit gap plus any overdue debt, which
   * is what the contract accepts. The modal re-quotes this from the server before sending;
   * this local value is what the button can show without a round trip.
   */
  topUpAmount: bigint;
}

/**
 * Read the deposit ledger straight from the mirror. Separate from `overdueClockView`
 * because it needs no clock: screens that only show what was charged (the bill, the
 * top-up modal) must not depend on the current time.
 *
 * `chargedDays` is derived from money, never from a counter: on v2.2 the ledger says
 * through which MOMENT overdue time is paid, so the days it covers are
 * `(paidThrough − episodeBase) / 24h` — which is exactly `paidMoney / dailyRate` with the
 * carry accounted for, and unlike v2.1's cumulative counter it belongs to the current
 * episode. v2.1 deals keep reading their counter.
 */
export const depositLedgerOf = (
  state: ApiRentalContractState | null | undefined,
  outstandingOverdue: bigint = 0n
): RentalDepositLedger => {
  if (!state) {
    return {
      isDailyModel: false,
      isPaidThroughModel: false,
      depositAmount: 0n,
      remainingDeposit: 0n,
      chargedAmount: 0n,
      chargedDays: 0,
      paidDaysTotal: 0,
      carryPaid: 0n,
      depositGap: 0n,
      topUpAmount: 0n,
    };
  }

  const version = state.contractVersion ?? 1;
  const isDailyModel = version >= 2;
  const isPaidThroughModel = version >= 3;
  const depositAmount = toBigInt(state.depositAmount);
  const remainingDeposit = toBigInt(state.remainingDeposit);
  const depositGap = depositAmount > remainingDeposit ? depositAmount - remainingDeposit : 0n;
  const chargedAmount = toBigInt(state.totalPenaltyCharged);
  const dailyRate = isDailyModel ? toBigInt(state.dailyOverdueRate) : 0n;

  return {
    isDailyModel,
    isPaidThroughModel,
    depositAmount,
    remainingDeposit,
    chargedAmount,
    chargedDays: paidOverdueDays(state),
    paidDaysTotal: isPaidThroughModel
      ? dailyRate > 0n
        ? Number(chargedAmount / dailyRate)
        : 0
      : Number(state.overdueDaysCharged ?? '0'),
    carryPaid: isPaidThroughModel ? toBigInt(state.overdueCarryPaid) : 0n,
    depositGap,
    topUpAmount: depositGap + outstandingOverdue,
  };
};

/** Where the current overdue episode's clock starts: the on-chain end plus the grace. */
const overdueBaseOf = (state: ApiRentalContractState): number =>
  Number(state.rentalEnd || '0') + OVERDUE_GRACE_SECONDS;

/**
 * Days of the current overdue episode that are fully paid for. v2.2 reads the "paid
 * through" mark (money, expressed as time); v2.1 reads its day counter; v1 has neither.
 */
const paidOverdueDays = (state: ApiRentalContractState): number => {
  const version = state.contractVersion ?? 1;

  if (version < 2) {
    return 0;
  }

  if (version < 3) {
    return Number(state.overdueDaysCharged ?? '0');
  }

  const paidThrough = Number(state.overdueChargedThrough ?? '0');
  const base = overdueBaseOf(state);

  // A mark at or before this episode's base means nothing is paid for in it yet — either
  // nothing was ever charged, or the mark belongs to an episode before the last extension.
  return paidThrough > base ? Math.floor((paidThrough - base) / DAY_SECONDS) : 0;
};

/**
 * What the renter still owes for overdue time that has already started (v2.2 §2.2),
 * mirroring the contract's own arithmetic: started days × the rate, minus the days already
 * paid through, minus the carry on the day being paid for. 0 on older generations, and 0
 * whenever the charging window is closed — the same window `applyOverdueCharge()` guards.
 *
 * A quote, not an authority: the transaction amount is re-read from the server, which asks
 * the contract.
 */
export const outstandingOverdueOf = (
  state: ApiRentalContractState | null | undefined,
  nowSeconds: number
): bigint => {
  if (!state || (state.contractVersion ?? 1) < 3) {
    return 0n;
  }

  const clockRunning = state.isActive && !state.isReturned && !state.isInspection && !state.isCompleted;
  const base = overdueBaseOf(state);

  if (!clockRunning || nowSeconds <= base) {
    return 0n;
  }

  const rate = toBigInt(state.dailyOverdueRate);

  if (rate <= 0n) {
    return 0n;
  }

  const startedCost = BigInt(Math.floor((nowSeconds - base) / DAY_SECONDS) + 1) * rate;
  const paidCost = BigInt(paidOverdueDays(state)) * rate + toBigInt(state.overdueCarryPaid);

  return startedCost > paidCost ? startedCost - paidCost : 0n;
};

/**
 * Derive the overdue view from the on-chain mirror.
 *
 * `nowSeconds` is passed in (never read from the clock here) so the screen can tick it and
 * the tests can place it exactly on a day boundary.
 */
export const overdueClockView = (
  state: ApiRentalContractState | null | undefined,
  nowSeconds: number
): RentalOverdueClockView => {
  if (!state) {
    return EMPTY_VIEW;
  }

  const rentalEnd = Number(state.rentalEnd || '0');
  const outstandingOverdue = outstandingOverdueOf(state, nowSeconds);
  const {
    isDailyModel,
    isPaidThroughModel,
    depositAmount,
    remainingDeposit,
    chargedAmount,
    chargedDays,
    depositGap,
    topUpAmount,
  } = depositLedgerOf(state, outstandingOverdue);
  const dailyRate = isDailyModel ? toBigInt(state.dailyOverdueRate) : 0n;

  // The same window `applyOverdueCharge()` guards: charging stops the moment the car is
  // returned, whatever the wall clock says.
  const clockRunning = state.isActive && !state.isReturned && !state.isInspection && !state.isCompleted;

  const graceEndsAt = rentalEnd + OVERDUE_GRACE_SECONDS;
  const isInGrace = clockRunning && nowSeconds > rentalEnd && nowSeconds <= graceEndsAt;
  const isOverdue = clockRunning && nowSeconds > graceEndsAt;
  // The contract's own formula, applied to the same `now`: day 1 the instant grace expires,
  // one more for every full 24h after that.
  const startedDays = isOverdue ? Math.floor((nowSeconds - graceEndsAt) / DAY_SECONDS) + 1 : 0;

  return {
    isDailyModel,
    isInGrace,
    isOverdue,
    secondsToEnd: Math.max(0, rentalEnd - nowSeconds),
    secondsToFirstCharge: Math.max(0, graceEndsAt - nowSeconds),
    startedDays,
    chargedDays,
    chargedAmount,
    remainingDeposit,
    depositAmount,
    dailyRate,
    hasUnchargedDay: isDailyModel && startedDays > chargedDays,
    outstandingOverdue,
    // Tied to the same running clock as the charging itself: a deposit emptied by an
    // approved defect claim after the car came back is spent, not "exhausted".
    // On v2.2 an empty deposit with a debt on top is the exhausted case even before a
    // whole day has been paid for; the charge that emptied it may have covered only part
    // of a day (the carry), which no longer counts as a charged day.
    isExhausted:
      isDailyModel &&
      clockRunning &&
      remainingDeposit === 0n &&
      (chargedDays > 0 || (isPaidThroughModel && chargedAmount > 0n)),
    isDepositFull: depositGap === 0n,
    topUpAmount,
    // Past the end date the contract requires BOTH a whole deposit and a settled overdue.
    needsTopUpForExtension: clockRunning && nowSeconds > rentalEnd && topUpAmount > 0n,
  };
};

/**
 * When an unpaid booking is cancelled by the platform cron: 30 minutes after it was created
 * (spec §4). Returns unix seconds, or null when the deal carries no usable timestamp — an
 * older api does not declare `createdAt`, and a missing deadline must hide the countdown
 * rather than show a wrong one.
 */
export const bookingPaymentDeadline = (createdAt: string | Date | undefined | null): number | null => {
  if (!createdAt) {
    return null;
  }

  const created = new Date(createdAt).getTime();

  if (Number.isNaN(created)) {
    return null;
  }

  return Math.floor(created / 1000) + BOOKING_PAYMENT_TTL_SECONDS;
};

/**
 * Coarse countdown text. Seconds are shown only under an hour — that is the booking
 * deadline, where they matter; longer spans read better without a ticking seconds digit.
 */
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const days = Math.floor(total / DAY_SECONDS);
  const hours = Math.floor((total % DAY_SECONDS) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const secs = total % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  return `${minutes}m ${String(secs).padStart(2, '0')}s`;
};
